import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { CardContent, CardHeader } from "../../../components/ui/card";
import { Check } from "lucide-react";
import { DOMAIN_CONFIG, getPortalUrl } from "../../../config/domains";
import { getPublicPricingPlans } from "../../../services/paymentService";
import { shouldUsePaystack, getUserRegion } from "../../../utils/locationUtils";

const Pricing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usePaystack, setUsePaystack] = useState(false);
  const [userRegion, setUserRegion] = useState('');
  const [locationLoading, setLocationLoading] = useState(true);

  // Detect user location and determine payment provider
  useEffect(() => {
    const detectLocationAndProvider = async () => {
      try {
        setLocationLoading(true);
        
        const shouldUsePaystackProvider = await shouldUsePaystack(usePaystack);
        const region = await getUserRegion();
        
        setUsePaystack(shouldUsePaystackProvider);
        setUserRegion(region);
      } catch (err) {
        console.warn('Location detection failed:', err);
        // Default to Stripe if location detection fails
        setUsePaystack(false);
        setUserRegion('United States');
      } finally {
        setLocationLoading(false);
      }
    };

    detectLocationAndProvider();
  }, []);

  // Fetch pricing plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      if (locationLoading) return; // Wait for location detection

      try {
        setLoading(true);
        setError(null);
        
        const plansData = await getPublicPricingPlans(usePaystack);

        // Ensure plans is an array
        const plans = Array.isArray(plansData) ? plansData : (plansData ? [plansData] : []);

        // Define the Free Playground plan (always shown first)
        const freePlaygroundPlan = {
          name: "Free Playground",
          price: 0,
          period: "Per purchase",
          features: ["Limited Tokens", "Rate limited access for testing"],
          id: "free-playground",
        };

        // Check if Free Playground already exists in the API response
        const hasFreePlayground = plans.some(plan =>
          (plan.name || "").toLowerCase().includes("free playground") ||
          (plan.name || "").toLowerCase().includes("free") && (plan.price === 0 || plan.price === "$0")
        );

        // Add Free Playground plan if it doesn't exist
        const allPlans = hasFreePlayground ? plans : [freePlaygroundPlan, ...plans];

        // Transform API response to component format
        const transformedPlans = allPlans.map((plan, index) => {
          // Get plan name lowercase for checks
          const planNameLower = (plan.name || "").toLowerCase();

          // Determine card background color based on plan name or index
          // Free Playground gets light green, others cycle through colors
          const cardBgColors = [
            "bg-[#DCEDE8]", // Light green for Free Playground
            "bg-[#FFF3C2]", // Light yellow
            "bg-[#FFE7D0]", // Light orange/peach
            "bg-[#FFE7D0]", // Light orange/peach
            "bg-[#DCEDE8]", // Light green
          ];

          // Free Playground should always be light green
          const isFreePlan = planNameLower.includes("free playground") ||
            (planNameLower.includes("free") && (plan.price === 0 || plan.price === "$0" || plan.amount === 0));
          const cardBg = isFreePlan ? "bg-[#DCEDE8]" : cardBgColors[index % cardBgColors.length];

          // Format price - handle both USD (Stripe) and NGN (Paystack)
          const price = plan.price || plan.amount || plan.priceAmount;
          let formattedPrice;

          // Handle features - could be an array or string
          let features = [];
          if (Array.isArray(plan.features)) {
            features = plan.features;
          } else if (plan.description) {
            features = [plan.description];
          } else if (plan.tokens || plan.characters) {
            features = [];
            // Get tokens - could be number or string
            const tokens = typeof plan.tokens === 'number' ? plan.tokens :
              typeof plan.tokens === 'string' ? parseInt(plan.tokens.replace(/,/g, '')) : 0;

            // Get characters - calculate from tokens if not provided (tokens * 4)
            let characters;
            if (plan.characters) {
              characters = typeof plan.characters === 'number' ? plan.characters :
                typeof plan.characters === 'string' ? parseInt(plan.characters.replace(/,/g, '')) : 0;
            } else if (tokens > 0) {
              // Calculate characters from tokens (1 token = 4 characters)
              characters = Math.round(tokens * 4);
            } else {
              characters = null;
            }

            // Add tokens to features
            if (tokens > 0) {
              features.push(`${tokens.toLocaleString()} Tokens`);
            } else if (plan.tokens) {
              features.push(`${plan.tokens} Tokens`);
            }

            // Add characters to features
            if (characters && characters > 0) {
              features.push(`${characters.toLocaleString()} Characters`);
            } else if (plan.characters && !characters) {
              features.push(`${plan.characters} Characters`);
            }
          } else if (plan.name === "Free Playground") {
            // Default features for Free Playground if not provided
            features = ["Limited Tokens", "Rate limited access for testing"];
          }

          // Check if plan is custom/development support plan
          const isCustomPlan = planNameLower.includes("custom") ||
            planNameLower.includes("development support") ||
            planNameLower.includes("api integration");

          // Check if plan is enterprise plan
          const isEnterprisePlan = planNameLower.includes("enterprise");

          // Format price for enterprise plans - show $299+ or equivalent
          if (isEnterprisePlan) {
            if (usePaystack) {
              // Convert $299 to NGN (approximately 1 USD = 1500 NGN, so $299 = ~448,500 NGN)
              // Using a more conservative rate: 1 USD = 1400 NGN
              const nairaAmount = Math.round(299 * 1400);
              formattedPrice = `₦${nairaAmount.toLocaleString()}+`;
            } else {
              formattedPrice = "$299+";
            }
          } else if (isFreePlan) {
            formattedPrice = usePaystack ? "₦0" : "$0";
          } else if (typeof price === 'number') {
            // If Paystack (NGN), convert from kobo to naira and format
            if (usePaystack) {
              const nairaAmount = price / 100; // Convert from kobo to naira
              formattedPrice = `₦${nairaAmount.toLocaleString()}`;
            } else {
              // Stripe (USD) - price is already in dollars
              formattedPrice = `$${price}`;
            }
          } else if (price) {
            // If price is already a string, ensure correct currency symbol
            if (typeof price === 'string') {
              // If it already has currency symbol, use as is
              if (price.includes('$') || price.includes('₦')) {
                formattedPrice = price;
              } else {
                // Try to parse and format with correct currency
                const numPrice = parseFloat(price.replace(/[^\d.]/g, ''));
                if (!isNaN(numPrice)) {
                  if (usePaystack) {
                    const nairaAmount = numPrice / 100; // Assume kobo if Paystack
                    formattedPrice = `₦${nairaAmount.toLocaleString()}`;
                  } else {
                    formattedPrice = `$${numPrice}`;
                  }
                } else {
                  formattedPrice = price;
                }
              }
            } else {
              formattedPrice = price;
            }
          } else {
            formattedPrice = "Custom";
          }

          // Add Enterprise-specific feature if it's an Enterprise plan
          if (isEnterprisePlan) {
            const priorityAccessFeature = 'Priority access to NKENNEAi Product and Engineering teams';
            // Ensure the feature is not already in the list (case-insensitive check)
            const hasPriorityAccess = features.some(f => 
              f.toLowerCase().includes('priority access') || 
              f.toLowerCase().includes('product and engineering')
            );
            if (!hasPriorityAccess) {
              features = [...features, priorityAccessFeature];
            }
          }

          // Determine button text based on plan name
          let buttonText = "Purchase Tokens";
          if (planNameLower.includes("free")) {
            buttonText = "Try Free Playground";
          } else if (isCustomPlan || isEnterprisePlan) {
            buttonText = "Contact Us";
          } else {
            buttonText = isAuthenticated ? "Purchase Tokens" : "Login to Purchase";
          }

          return {
            id: plan.id || plan.stripePriceId,
            name: plan.name || plan.planName || "Plan",
            price: formattedPrice,
            period: plan.period || plan.billingPeriod || "Per purchase",
            features: features.length > 0 ? features : ["Dedicated integration support from NKENNEAi engineers, including onboarding guidance, technical reviews, and personalized implementation assistance."],
            buttonText,
            buttonVariant: planNameLower.includes("free") ? "outline" : "default",
            cardBg,
            isCustomPlan, // Flag to identify custom plan
            isEnterprisePlan, // Flag to identify enterprise plan
            // Keep original plan data for purchase
            originalPlan: plan,
          };
        });

        // Sort plans by price (lowest first)
        const sortedPlans = transformedPlans.sort((a, b) => {
          // Extract numeric price from formatted price strings
          const extractNumericPrice = (priceStr) => {
            if (!priceStr) return Infinity; // Custom/unknown prices go to end
            
            // Extract number from strings like "$10", "₦1000", "$299+", "₦418,600+", etc.
            // Remove all non-digit characters except decimal point, then parse
            const numericStr = priceStr.replace(/[^\d.]/g, '');
            const num = parseFloat(numericStr);
            
            // If includes "+" (like $299+), extract the number before the +
            // This allows enterprise plans to sort correctly among themselves
            if (priceStr.includes('+')) {
              return isNaN(num) ? Infinity : num;
            }
            
            return isNaN(num) ? Infinity : num;
          };
          
          const priceA = extractNumericPrice(a.price);
          const priceB = extractNumericPrice(b.price);
          
          return priceA - priceB;
        });

        setPricingPlans(sortedPlans);
      } catch (err) {
        console.error("Error fetching pricing plans:", err);
        setError(err.message || "Failed to load pricing plans. Please try again later.");

        // Fallback to default plans if API fails
        setPricingPlans([
          {
            name: "Free Playground",
            price: "$0",
            period: "Per purchase",
            features: ["Limited Tokens", "Rate limited access for testing"],
            buttonText: "Try Free Playground",
            buttonVariant: "outline",
            cardBg: "bg-[#DCEDE8]",
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [isAuthenticated, usePaystack, locationLoading]);

  const handlePlanSelection = (plan) => {
    // Use original plan data if available, otherwise use transformed plan
    const planData = plan.originalPlan || plan;
    const planNameLower = (plan.name || "").toLowerCase();

    // Check if this is a custom/development support plan
    const isCustomPlan = plan.isCustomPlan ||
      planNameLower.includes("custom") ||
      planNameLower.includes("development support") ||
      planNameLower.includes("api integration");

    // Check if this is an enterprise plan
    const isEnterprisePlan = plan.isEnterprisePlan ||
      planNameLower.includes("enterprise");

    if (planNameLower.includes("free") || plan.name === "Free Playground") {
      // Free Playground always goes to playground page (on same domain)
      navigate('/playground');
    } else if (isCustomPlan || isEnterprisePlan) {
      // Custom/Development Support/Enterprise plans redirect to contact us page
      navigate('/contact-us');
    } else {
      // For other paid plans, redirect to login page
      if (DOMAIN_CONFIG.IS_STATIC_SITE) {
        // On static site (nkenne.ai), redirect to portal login
        const loginUrl = getPortalUrl('/login');
        window.location.href = loginUrl;
      } else {
        // On portal site (developers.nkenne.ai), navigate to login
        navigate('/login');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background py-16">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block text-primary px-4 py-2 text-sm font-medium mb-4 font-semibold">
            PRICING PLANS
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Token-Based Pricing for Your Translation Needs
          </h1>
          <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
            Choose a plan that fits your translation needs and start translating
            text right away in Yoruba, Swahili, and Nigerian Pidgin today.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-auto max-w-3xl mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-primary"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && pricingPlans.length === 0 && !error && (
          <div className="text-center py-20">
            <p className="text-muted-foreground">No pricing plans available at the moment.</p>
          </div>
        )}

        {/* Pricing Grid */}
        {!loading && pricingPlans.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 lg:mx-[120px] mx-[20px]">
            {pricingPlans.map((plan, index) => (
              <div
                key={plan.id || plan.name || index}
                className={`${plan.cardBg} border-2 rounded-3xl relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-105 flex flex-col`}
              >
                <CardHeader className="text-left pb-4 border-b-2">
                  <h3 className="font-semibold text-foreground text-sm mb-2">
                    {plan.name}
                  </h3>
                  <div className="mb-2">
                    <span className="text-3xl font-bold text-foreground">
                      {plan.price}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm">{plan.period}</p>
                </CardHeader>

                <CardContent className="pt-0 mt-6 flex-1 flex flex-col">
                  <div className="mb-6 flex-1">
                    <h4 className="font-semibold text-foreground text-sm mb-3">
                      Key Features:
                    </h4>
                    <ul className="space-y-2">
                      {plan.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-center text-sm text-foreground"
                        >
                          <Check className="h-4 w-4 text-success mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    onClick={() => handlePlanSelection(plan)}
                    className="mt-auto p-2 rounded-md w-full bg-black hover:bg-primary/90 text-white transition-colors"
                  >
                    {plan.buttonText}
                  </button>
                </CardContent>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Pricing;
